import React, { type FC, memo, use, useMemo } from "react";
import { ProgressCircle } from "@heroui/react";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";

import fontColorContrast from "font-color-contrast";

import { Indicator } from "./Indicator";
import { ModuleMenu } from "./ModuleMenu";
import { AppsDrawer } from "./AppsDrawer";
import { CompanyContext } from "@domain/context/CompanyContext";
import { ReadinessContext } from "@domain/context/DataReadyness";
import { CompanyDropdown } from "./CompanyDropdown";
import { LangDropdown } from "./LangDropdown";
import { LoginLogout } from "./LoginLogout";
import { ReleaseNotesBell } from "./ReleaseNotesBell";
import { ModuleNav, type NavItem } from "./ModuleMenu/ModuleNav";
import { AppRoutes } from "@domain/routing/routeConstants";
import "./Header.css";

const Loader = memo(function Loader() {
    const { isReady, percent } = use(ReadinessContext);
    return (
        <div className={"Header--Loader " + (isReady ? "Header--Loader__hidden" : "")}>
            <ProgressCircle aria-label="Loading" value={percent} size="sm" color="accent" className="w-5 h-5">
                <ProgressCircle.Track>
                    <ProgressCircle.TrackCircle />
                    <ProgressCircle.FillCircle />
                </ProgressCircle.Track>
            </ProgressCircle>
        </div>
    );
});

const MetroAdminNavigation = memo(function MetroAdminNavigation() {
    const { pathname } = useLocation();
    const selectedKeys = useMemo(() => [pathname], [pathname]);
    const items = useMemo<NavItem[]>(
        () => [
            {
                key: AppRoutes.adminMetroAccountingCategories,
                to: AppRoutes.adminMetroAccountingCategories,
                label: <FormattedMessage id="app.companies.metroAccountingCategories" />,
            },
            {
                key: AppRoutes.adminMetroArticles,
                to: AppRoutes.adminMetroArticles,
                label: <FormattedMessage id="app.companies.metroArticles" />,
            },
        ],
        [],
    );
    const isMetroAdminPage =
        pathname === AppRoutes.adminMetroAccountingCategories || pathname === AppRoutes.adminMetroArticles;

    return isMetroAdminPage ? <ModuleNav selectedKeys={selectedKeys} items={items} /> : null;
});

const HeaderImpl: FC = () => {
    const { companyGQL } = use(CompanyContext);
    const color = companyGQL?.color;
    const style = useMemo(
        () => (color ? { backgroundColor: color, color: fontColorContrast(color) } : undefined),
        [color],
    );

    return (
        <header style={style} className="Header flex justify-between items-center px-4">
            <div className="Header--Navigation">
                <AppsDrawer />
                <ModuleMenu />
                <MetroAdminNavigation />
            </div>
            <div className="Header--SelectorsGroup">
                <CompanyDropdown />
                <LangDropdown />
                <ReleaseNotesBell />
                <LoginLogout />
                <Indicator />
                <Loader />
            </div>
        </header>
    );
};

export const Header = memo(HeaderImpl);
